import { MM_PER_INCH, POINTS_PER_INCH } from '@fotoboek/domain';

export function mmToInch(mm: number): number {
  return mm / MM_PER_INCH;
}

export function inchToMm(inch: number): number {
  return inch * MM_PER_INCH;
}

export function mmToPx(mm: number, dpi: number): number {
  return (mm / MM_PER_INCH) * dpi;
}

export function pxToMm(px: number, dpi: number): number {
  return (px / dpi) * MM_PER_INCH;
}

export function ptToMm(pt: number): number {
  return (pt / POINTS_PER_INCH) * MM_PER_INCH;
}

export function mmToPt(mm: number): number {
  return (mm / MM_PER_INCH) * POINTS_PER_INCH;
}

export type Size = { width: number; height: number };
export type Rect = { x: number; y: number; width: number; height: number };

export type FitResult = {
  /** Drawn image width in frame units */
  width: number;
  /** Drawn image height in frame units */
  height: number;
  /** Top-left x of image within frame before user offset */
  x: number;
  /** Top-left y of image within frame before user offset */
  y: number;
  /** Scale applied relative to natural image size in frame units */
  scale: number;
};

/**
 * Deterministic cover fit: image fills the frame, cropping overflow.
 */
export function fitCover(frame: Size, image: Size): FitResult {
  const scale = Math.max(frame.width / image.width, frame.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    width,
    height,
    x: (frame.width - width) / 2,
    y: (frame.height - height) / 2,
    scale,
  };
}

/**
 * Deterministic contain fit: image fits entirely inside the frame.
 */
export function fitContain(frame: Size, image: Size): FitResult {
  const scale = Math.min(frame.width / image.width, frame.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    width,
    height,
    x: (frame.width - width) / 2,
    y: (frame.height - height) / 2,
    scale,
  };
}

export type Placement = {
  scale: number;
  offsetX: number;
  offsetY: number;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
};

/**
 * Apply user placement on top of cover fit.
 * offsetX/Y are normalized relative to leftover (image - frame) range when cropped.
 */
export function applyPhotoPlacement(
  frame: Size,
  image: Size,
  placement: Placement,
): FitResult & { flipX: boolean; flipY: boolean; rotation: number } {
  const base = fitCover(frame, image);
  const width = base.width * placement.scale;
  const height = base.height * placement.scale;
  const leftoverX = Math.max(0, (width - frame.width) / 2);
  const leftoverY = Math.max(0, (height - frame.height) / 2);
  const x = (frame.width - width) / 2 + placement.offsetX * leftoverX;
  const y = (frame.height - height) / 2 + placement.offsetY * leftoverY;
  return {
    width,
    height,
    x,
    y,
    scale: base.scale * placement.scale,
    flipX: placement.flipX,
    flipY: placement.flipY,
    rotation: placement.rotation,
  };
}

export type GuideRects = {
  trim: Rect;
  bleed: Rect;
  safe: Rect;
  content: Rect;
};

/** Page coordinate system: (0,0) is top-left of trim box (finished page). */
export function pageGuides(pageSize: Size, bleedMm: number, safeMarginMm: number): GuideRects {
  return {
    trim: { x: 0, y: 0, width: pageSize.width, height: pageSize.height },
    bleed: {
      x: -bleedMm,
      y: -bleedMm,
      width: pageSize.width + bleedMm * 2,
      height: pageSize.height + bleedMm * 2,
    },
    safe: {
      x: safeMarginMm,
      y: safeMarginMm,
      width: pageSize.width - safeMarginMm * 2,
      height: pageSize.height - safeMarginMm * 2,
    },
    content: { x: 0, y: 0, width: pageSize.width, height: pageSize.height },
  };
}

export function spreadSize(pageSize: Size, gutterMm = 0): Size {
  return {
    width: pageSize.width * 2 + gutterMm,
    height: pageSize.height,
  };
}

export function elementCenter(rect: Rect): { x: number; y: number } {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

export function rotatePoint(
  point: { x: number; y: number },
  origin: { x: number; y: number },
  degrees: number,
): { x: number; y: number } {
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: origin.x + dx * cos - dy * sin,
    y: origin.y + dx * sin + dy * cos,
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
